import {action, observable, runInAction} from 'mobx';
import {IWebRtcDetector} from '@techsee/techsee-client-infra/lib/services/DetectWebRtcService';
import {ClientInfo} from '@techsee/techsee-common/lib/data-contracts/ClientInfo';
import {PlatformType} from '@techsee/techsee-common/lib/constants/utils.constant';
import {getBrowserInfo} from '@techsee/techsee-client-infra/lib/shared/browser-info';
import {AppState, IAppUser} from '../../app.state';
import {getRootStore} from '../../app.bootstrap';
import {MultipartyDashboardController} from './MultipartyDashboardController';
import {MultipartyDashboardController as NWayMultipartyDashboardController} from './nwayDashboard/MultipartyDashboardController';
import {EventLogConstant} from '../../constants/events/event-log.constant';
import assign from 'lodash/assign';
import {IEventLogsService} from '../../services/EventsLogService';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {getDashboardLanguage} from '../helper/general.helper';
import {ITsLocaleHelperService} from '../../services/ts-locale-helper/ts-locale-helper.service';
import {IChatApi} from '../../services/ts-chat-api/ChatApi';
import {getDashboardStores} from '../dashboard/dashboard.stores';
import {InteractionSummaryHelperService} from '../../services/InteractionSummaryHelperService';
import {IEventLogService} from '../../services/event-log/event-log.service';
import {IInteractionSummaryService} from '@techsee/techsee-client-infra/lib/services/InteractionSummaryService';
import {IRouteStore} from '../../router/routing.infra';
import {getAppTracer} from '../../../app.tracer';
import get from 'lodash/get';

const trace = getAppTracer('MultipartyDashboardStores');

export class MultipartyDashboardStores implements IRouteStore {
    @observable public dashboardController?: MultipartyDashboardController | NWayMultipartyDashboardController;
    @observable public videoController?: any;
    @observable public galleryController?: any;
    @observable public localizationService?: any;
    @observable public applicationState?: AppState;
    @observable public leftBarController?: any;
    @observable public liveLeftBarController?: any;
    @observable public liveTopBarController?: any;
    @observable public sessionEndModalController?: any;
    @observable public customerEndedTheMeetingModalController?: any;
    @observable public clientWebRtcNotSupportedModalController?: any;
    @observable public shareScreenPopupController?: any;
    @observable public remoteControlPopupController?: any;

    private _logEvents: EventLogConstant;
    private _eventsLogService: IEventLogsService;
    private _eventLogService: IEventLogService;
    private _interactionSummaryService: IInteractionSummaryService;
    private readonly _tsLocaleHelper: ITsLocaleHelperService;
    private _tsChatApi: IChatApi;

    constructor() {
        this._tsChatApi = getRootStore().chatApi;
        this._logEvents = EventLogConstant;
        this._eventsLogService = getRootStore().eventsLogService;
        this._eventLogService = getRootStore().eventLogService;
        this._interactionSummaryService = getRootStore().interactionSummaryService;
        this._tsLocaleHelper = getRootStore().tsLocaleHelper;
    }

    private get dashboardState() {
        return getDashboardStores().dashboardState;
    }

    public dispose(): void {
        if (this.dashboardController && typeof this.dashboardController.dispose === 'function') {
            this.dashboardController.dispose();
        }
    }

    public async initStores(): Promise<void> {
        const {appState, localizationService, webRtcDetector} = getRootStore();

        this._eventsLogService.room = this.dashboardState.roomData._id;

        this.updateAppState(appState);
        await this.webRtcSupport();

        localizationService.setAccountData(appState.accountId, appState.dashboardLanguage);

        await this.getClientInfo(webRtcDetector);
        await localizationService.init();

        const interactionSummaryHelperService = new InteractionSummaryHelperService(
            this._interactionSummaryService,
            this.dashboardState.accountData,
            this.dashboardState.roomData,
            localizationService.translate,
            this._eventLogService
        );

        const tsSharingService = getDashboardStores().sharingService;
        const tsReactStateHelper = getRootStore().tsReactStateHelper;

        const controller =
            this.dashboardState.accountData.protectedSettings.enableNWay &&
            (this.dashboardState.roomData.conferencingMode ||
                this.dashboardState.roomData.conferencingMode === undefined)
                ? new NWayMultipartyDashboardController(
                      this._tsLocaleHelper,
                      this._tsChatApi,
                      this.dashboardState,
                      this._logEvents,
                      tsReactStateHelper,
                      tsSharingService
                  )
                : new MultipartyDashboardController(
                      this._tsLocaleHelper,
                      this._tsChatApi,
                      this.dashboardState,
                      this._logEvents,
                      tsReactStateHelper,
                      tsSharingService,
                      getRootStore().interactionSummaryTranslated,
                      interactionSummaryHelperService
                  );

        runInAction(() => {
            this.dashboardController = controller;
            this.localizationService = localizationService;
            this.applicationState = appState;
            this.videoController = controller.videoController;
            this.galleryController = controller.galleryController;
            this.leftBarController = controller.leftBarController;
            this.liveLeftBarController = controller.liveLeftBarController;
            this.liveTopBarController = controller.liveTopBarController;
            this.sessionEndModalController = controller.sessionEndModalController;
            this.customerEndedTheMeetingModalController = controller.customerEndedTheMeetingModalController;
            this.clientWebRtcNotSupportedModalController = controller.clientWebRtcNotSupportedModalController;
            this.shareScreenPopupController = controller.shareScreenPopupController;

            if (controller instanceof MultipartyDashboardController) {
                this.remoteControlPopupController = (
                    controller as MultipartyDashboardController
                ).remoteControlPopupController;
            }
        });

        const guid = get(this.dashboardState.roomData, 'guids[0]', '');

        if (guid) {
            trace.info('Participant link', {url: `${window.location.origin}/nway?g=${guid}`});
        }
    }

    private webRtcSupport() {
        return getRootStore()
            .webRtcDetector.getWebRtcInfo()
            .then((webRTCSupportResult) => {
                this._tsChatApi.setWebRTCSupport(webRTCSupportResult);
                this._eventsLogService.info({
                    logType: LOG_EVENTS.webRTCSupported,
                    userId: this.dashboardState.currentUser._id,
                    accountId: this.dashboardState.currentUser.accountId,
                    meta: assign(webRTCSupportResult, {side: PlatformType.dashboard})
                });
            });
    }

    @action
    private updateAppState(appState: AppState): void {
        appState.accountId = this.dashboardState.accountData._id;
        appState.dashboardLanguage = getDashboardLanguage(
            this.dashboardState.accountData,
            this.dashboardState.roomData
        );
        appState.accountName = this.dashboardState.accountData.companyName;
        appState.setAppUser(this.dashboardState.currentUser as IAppUser);
    }

    private async getClientInfo(detectRTC: IWebRtcDetector): Promise<ClientInfo> {
        const webRtcInfo = await detectRTC.getWebRtcInfo();
        const clientInfo: ClientInfo = {
            platformType: PlatformType.dashboard,
            clientVersion: CLIENT_VERSION,
            browserInfo: getBrowserInfo(),
            webRtcInfo: webRtcInfo
        };

        return clientInfo;
    }
}
